import React, { useCallback, useEffect, useState } from 'react';
import { PatientTypeSelection } from './Patient/PatientTypeSelection';
import { HubAssignmentConfirmation } from './Patient/HubAssignmentConfirmation';
import HubAssignment from './Patient/HubAssignment';
import { StatusMessage } from '../../../Hubs/HubDetails/AssignHubController/components/StatusMessage';
import { NewPatientForm } from '../../../Hubs/HubDetails/AssignHubController/components/NewPatientForm';
import { ReactComponent as UpdateSwSuccessIcon } from '../../../../images/icons/update_sw_success.svg';
import { ReactComponent as FailedToAddUserIcon } from '../../../../images/icons/adding_user_failed.svg';
import { ReactComponent as HubAssignFailedIcon } from '../../../../images/icons/hub_assign_failed.svg';
import { ReactComponent as ProfilePicErrorIcon } from '../../../../images/icons/photo_add_failed.svg';
import { Box, CircularProgress } from '@mui/material';
import { HubApiActions } from '../../../../store/hubOwner/types';
import {
  addHubOwner,
  clearHubActionStatus,
} from '../../../../store/hubOwner/actionCreators';
import {
  clearCustomerActionStatus,
  uploadProfilePicture,
} from '../../../../store/customers/actionCreators';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import {
  clearDeviceActionStatus,
  linkDeviceToOwner,
} from '../../../../store/devices/actionCreators';
import { DeviceActionType } from '../../../../shared/constants';

const STEPS = {
  TYPE_SELECTION: 'type_selection',
  PATIENT_DETAILS: 'patient_details',
  HUB_CONFIRMATION: 'hub_confirmation',
  HUB_ASSIGNMENT: 'hub_assignment',
  SUCCESS: 'success',
  ERROR: 'error',
};

const AddPatientController = ({
  onClose,
  onBack,
  onStageChange,
  onPatientAdded,
  isFacilityAdmin,
}: {
  onClose: () => void;
  onBack: () => void;
  onStageChange: (stage: string) => void;
  onPatientAdded?: () => void; // used to refresh patient list
  isFacilityAdmin?: boolean; // used to customize patient flow for facility admins
}) => {
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState(STEPS.TYPE_SELECTION);
  const [patientType, setPatientType] = useState<
    'facility' | 'independent' | null
  >(isFacilityAdmin ? 'facility' : null);
  const [newOwnerId, setNewOwnerId] = useState<number | null>(null);
  const [addPhoto, setAddPhoto] = useState<File | null>(null);
  const [isPhotoUploading, setIsPhotoUploading] = useState(false);
  const [facilityId, setFacilityId] = useState<number | null>(null);
  // Duplicate-check follow-up (409 action=get_phone): re-open the form with
  // the submitted values and a now-required phone field
  const [requirePhone, setRequirePhone] = useState(false);
  const [lastSubmittedValues, setLastSubmittedValues] = useState<any | null>(
    null,
  );

  const { isAddingHubOwner, actionStatus: hubOwnerActionStatus } = useSelector(
    (state: RootState) => state.hubOwner,
  );

  const {
    actionStatus: customerActionStatus,
    isLoading: profileUploadLoading,
  } = useSelector((state: RootState) => state.customers);

  const { actionStatus: deviceActionStatus } = useSelector(
    (state: RootState) => state.devices,
  );

  const handlePatientTypeSelect = (type: 'facility' | 'independent') => {
    onStageChange(STEPS.PATIENT_DETAILS);
    setPatientType(type);
    setCurrentStep(STEPS.PATIENT_DETAILS);
  };

  const handlePatientSubmit = (values: any) => {
    const { photo, isFromFacility, facility_location_id, ...patientData } =
      values;

    // Store facility_id for hub assignment filter
    if (patientType === 'facility' && values.facility_location_id) {
      setFacilityId(Number(values.facility_location_id));
    }

    const payload = {
      ...patientData,
      facility_id:
        patientType === 'facility' ? values.facility_location_id : undefined,
    };

    setAddPhoto(photo || null);
    setLastSubmittedValues(values);
    dispatch(addHubOwner(payload));
  };

  // Handle Hub Owner status changes
  useEffect(() => {
    if (hubOwnerActionStatus.success) {
      if (!addPhoto) {
        // Success without photo
        setCurrentStep(STEPS.HUB_CONFIRMATION);
        setNewOwnerId(hubOwnerActionStatus.data);
      } else if (!isPhotoUploading && hubOwnerActionStatus.data) {
        // Success with photo - trigger photo upload
        setIsPhotoUploading(true);
        dispatch(uploadProfilePicture(hubOwnerActionStatus.data, addPhoto));
      }
    } else if (hubOwnerActionStatus.error) {
      if (hubOwnerActionStatus.errorAction === 'get_phone') {
        // Possible duplicate — send the user back to the form to add a
        // phone number for verification instead of showing an error screen
        dispatch(clearHubActionStatus());
        setRequirePhone(true);
        setCurrentStep(STEPS.PATIENT_DETAILS);
      } else {
        setCurrentStep(STEPS.ERROR);
      }
    }
  }, [hubOwnerActionStatus, addPhoto, dispatch, isPhotoUploading]);

  // Handle Profile Picture upload status
  useEffect(() => {
    if (customerActionStatus.success && isPhotoUploading) {
      setCurrentStep(STEPS.HUB_CONFIRMATION);
      setNewOwnerId(hubOwnerActionStatus.data);
      setIsPhotoUploading(false);
      setAddPhoto(null);
    } else if (customerActionStatus.error && isPhotoUploading) {
      setCurrentStep(STEPS.ERROR);
      setIsPhotoUploading(false);
    }
  }, [customerActionStatus, hubOwnerActionStatus.data, isPhotoUploading]);

  const handleClearCatch = useCallback(() => {
    dispatch(clearCustomerActionStatus());
    dispatch(clearHubActionStatus());
    dispatch(clearDeviceActionStatus());
    setIsPhotoUploading(false);
    setAddPhoto(null);
    setRequirePhone(false);
    setLastSubmittedValues(null);
    onClose();
  }, [dispatch, onClose]);

  const handleSuccessClose = () => {
    if (onPatientAdded) onPatientAdded();
    handleClearCatch();
  };

  const handleHubConfirmation = (wantHub: boolean) => {
    if (wantHub) {
      setCurrentStep(STEPS.HUB_ASSIGNMENT);
    } else {
      setNewOwnerId(null);
      setCurrentStep(STEPS.SUCCESS);
    }
  };

  const handleHubAssignment = (hubId: number) => {
    if (!newOwnerId) return;
    dispatch(linkDeviceToOwner(hubId, newOwnerId));
  };

  //  to handle handleHubAssignment action status
  useEffect(() => {
    if (deviceActionStatus.type === DeviceActionType.LinkDeviceToOwner) {
      if (deviceActionStatus.success) {
        setCurrentStep(STEPS.SUCCESS);
      } else {
        setCurrentStep(STEPS.ERROR);
      }
    }
  }, [deviceActionStatus]);

  const renderStep = () => {
    if (isAddingHubOwner || profileUploadLoading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={200}
        >
          <CircularProgress />
        </Box>
      );
    }

    // Facility admin: skip PatientTypeSelection
    if (isFacilityAdmin && currentStep === STEPS.TYPE_SELECTION) {
      setCurrentStep(STEPS.PATIENT_DETAILS);
      onStageChange(STEPS.PATIENT_DETAILS);
      return null;
    }

    switch (currentStep) {
      case STEPS.TYPE_SELECTION:
        if (isFacilityAdmin) {
          // Should never reach here, but just in case
          setCurrentStep(STEPS.PATIENT_DETAILS);
          onStageChange(STEPS.PATIENT_DETAILS);
          return null;
        }
        onStageChange('initial');
        return (
          <PatientTypeSelection
            onSelect={handlePatientTypeSelect}
            onBack={onBack}
          />
        );
      case STEPS.PATIENT_DETAILS:
        return (
          <NewPatientForm
            onAdd={handlePatientSubmit}
            onCancel={() => {
              if (isFacilityAdmin) {
                onBack();
              } else {
                onStageChange('initial');
                setCurrentStep(STEPS.TYPE_SELECTION);
              }
            }}
            isFromFacility={patientType === 'facility'}
            setCurrentStep={(step: string) => {
              if (step === 'new_patient') {
                onStageChange(STEPS.PATIENT_DETAILS);
              } else {
                onStageChange('initial');
              }
            }}
            isFacilityAdmin={isFacilityAdmin}
            requirePhone={requirePhone}
            prefillValues={lastSubmittedValues}
          />
        );
      case STEPS.HUB_CONFIRMATION:
        onStageChange('initial');
        return (
          <HubAssignmentConfirmation
            onConfirm={handleHubConfirmation}
            onBack={() => {
              onStageChange(STEPS.PATIENT_DETAILS);
              setCurrentStep(STEPS.PATIENT_DETAILS);
            }}
          />
        );
      case STEPS.HUB_ASSIGNMENT:
        onStageChange('initial');
        return (
          <HubAssignment
            onSubmit={handleHubAssignment}
            onBack={() => {
              onStageChange('initial');
              setCurrentStep(STEPS.HUB_CONFIRMATION);
            }}
            facilityId={facilityId}
          />
        );
      case STEPS.SUCCESS:
        onStageChange('initial');
        return (
          <StatusMessage
            icon={<UpdateSwSuccessIcon />}
            title="Added successfully!"
            onClose={handleSuccessClose}
          />
        );
      case STEPS.ERROR:
        onStageChange('initial');
        return (
          <StatusMessage
            icon={
              hubOwnerActionStatus.apiAction === HubApiActions.ADD_HUB_OWNER ? (
                <FailedToAddUserIcon />
              ) : isPhotoUploading ? (
                <ProfilePicErrorIcon />
              ) : (
                <HubAssignFailedIcon />
              )
            }
            title={
              hubOwnerActionStatus.apiAction === HubApiActions.ADD_HUB_OWNER
                ? 'Patient didn’t save'
                : isPhotoUploading
                  ? 'Photo didn’t save'
                  : 'Failed to assign Hub'
            }
            message={
              hubOwnerActionStatus.errorAction === 'contact_cc'
                ? 'Something went wrong. Please contact customer care.'
                : 'Please try again or come back later.'
            }
            onClose={() => {
              if (
                hubOwnerActionStatus.error &&
                hubOwnerActionStatus.apiAction === HubApiActions.ADD_HUB_OWNER
              ) {
                onStageChange(STEPS.PATIENT_DETAILS);
                dispatch(clearHubActionStatus());
                setCurrentStep(STEPS.PATIENT_DETAILS);
              } else {
                handleClearCatch();
              }
            }}
            primaryButtonText="Try again"
          />
        );
      default:
        return null;
    }
  };

  return renderStep();
};

export default AddPatientController;
